<template>
    <div class="question">
        <h1
            v-if="question.title"
            class="title"
        >
            {{ question.title }}
        </h1>

        <component
            :is="component"
            v-model:answer="modelAnswer"
            :question
            :focus
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import type { QuestionnaireQuestionFragment } from '@/graphql/api'
import { assertNever } from '@/helpers'
import { useQuestionnaireStore } from '@/components/cms/component/questionnaire/useQuestionnaireStore'

import QuestionnaireQuestionChoice from '@/components/cms/component/questionnaire/question/QuestionnaireQuestionChoice.vue'
import QuestionnaireQuestionText from '@/components/cms/component/questionnaire/question/QuestionnaireQuestionText.vue'
import QuestionnaireQuestionPrice from '@/components/cms/component/questionnaire/question/QuestionnaireQuestionPrice.vue'
import DebugComponent from '@/components/cms/component/DebugComponent.vue'

const props = defineProps<{
    question: QuestionnaireQuestionFragment
    stepIndex: number
    stepTitle: string
    focus: boolean
}>()

const component = computed((): any => {
    const type = props.question.__typename

    switch (type) {
        case 'QuestionnaireQuestionChoice':
            return QuestionnaireQuestionChoice
        case 'QuestionnaireQuestionText':
            return QuestionnaireQuestionText
        case 'QuestionnaireQuestionPrice':
            return QuestionnaireQuestionPrice
        default:
            return assertNever(type, DebugComponent)
    }
})

const questionnaireStore = useQuestionnaireStore()
const currentRoute = useRoute()
const questionnaireId = computed(
    () => (currentRoute.params['id'] || '__default__') as string
)

const modelAnswer = computed({
    get: () =>
        questionnaireStore.answers[questionnaireId.value]?.[props.question.id]
            ?.answer,
    set: (answer) => {
        if (answer === undefined) {
            return
        }

        questionnaireStore.setAnswer(
            answer,
            props.stepIndex,
            props.stepTitle,
            questionnaireId.value
        )
    },
})
</script>
<style lang="scss" scoped>
.question {
    padding: 20px 20px 40px;

    .title {
        font-family: $parcel-font-stack;
        font-style: normal;
        font-weight: 300;
        font-size: 1.2rem;
        line-height: 1.2em;
        width: 800px;
        max-width: 100%;
        color: $blue;
        margin: 40px auto 40px;

        @include min-width(768) {
            font-size: 1.5rem;
            text-align: center;
            margin: 40px auto 80px;
        }
    }
}
</style>
